/**
 * FAQ Accordion Component
 * Displays frequently asked questions in collapsible format
 */

'use client';

import styled from '@emotion/styled';
import { Accordion, useTheme } from '@template-project/afrodita-components';
import { ProductFAQ } from '@/domain/entities/product.entity';

export interface FAQAccordionProps {
  title?: string;
  faqs: ProductFAQ[];
}

const Container = styled.section`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.space['3xl']} ${theme.space.md}`};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.variants.heading2.fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  text-align: center;
  margin: ${({ theme }) => `0 0 ${theme.space['2xl']}`};
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    font-size: ${({ theme }) => theme.typography.variants.heading3.fontSize}px;
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
`;

const FAQItem = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  padding: ${({ theme }) => theme.space.md};
`;

const Question = styled.h3`
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Answer = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.text.secondary};

  p {
    margin: ${({ theme }) => theme.space.sm} 0;
  }
`;

const PlusIcon = styled.span`
  display: inline-block;
  width: 20px;
  height: 20px;
  position: relative;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    background-color: currentColor;
  }
  
  &::before {
    width: 20px;
    height: 2px;
    top: 9px;
    left: 0;
  }
  
  &::after {
    width: 2px;
    height: 20px;
    top: 0;
    left: 9px;
  }
`;

const MinusIcon = styled.span`
  display: inline-block;
  width: 20px;
  height: 2px;
  background-color: currentColor;
`;

export function FAQAccordion({
  title = "Questions? We've got the answers!",
  faqs,
}: FAQAccordionProps) {
  const theme = useTheme();

  if (!faqs || faqs.length === 0) {
    return null;
  }

  return (
    <Container>
      <Title>{title}</Title>
      <FAQList>
        {faqs.map((faq) => (
          <FAQItem key={faq.id}>
            <Accordion
              closeIcon={MinusIcon}
              content={
                <Answer
                  dangerouslySetInnerHTML={{ __html: faq.answer }}
                />
              }
              header={<Question>{faq.question}</Question>}
              openIcon={PlusIcon}
              variant="simple"
            />
          </FAQItem>
        ))}
      </FAQList>
    </Container>
  );
}

